import { ReportsResponse } from '../types/reports'

// Global cache for basic report data
export const basicReportCache = {
  data: null as ReportsResponse | null,
  timestamp: 0,
  lastCheck: 0,
  // Cache expires after 5 minutes
  CACHE_DURATION: 5 * 60 * 1000,
  // Check for updates every 30 seconds
  CHECK_INTERVAL: 30 * 1000,
}

// Check if we have valid cached data
export const getCachedBasicReports = (): ReportsResponse | null => {
  const now = Date.now()

  // Check if cache is valid and not expired
  if (basicReportCache.data && now - basicReportCache.timestamp < basicReportCache.CACHE_DURATION) {
    return basicReportCache.data
  }

  return null
}

// Update cache with new data
export const updateCache = (data: ReportsResponse) => {
  basicReportCache.data = data
  basicReportCache.timestamp = Date.now()
  basicReportCache.lastCheck = Date.now()
}

// Check if we need to fetch new data
export const shouldFetchNewData = (): boolean => {
  const now = Date.now()

  // If no cache exists, we need to fetch
  if (!basicReportCache.data) {
    return true
  }

  // If cache is expired, we need to fetch
  if (now - basicReportCache.timestamp >= basicReportCache.CACHE_DURATION) {
    return true
  }

  // If we haven't checked recently, we should check for updates
  if (now - basicReportCache.lastCheck >= basicReportCache.CHECK_INTERVAL) {
    return true
  }

  return false
}

// Clear the cache (useful for testing or manual refresh)
export const clearCache = () => {
  basicReportCache.data = null
  basicReportCache.timestamp = 0
  basicReportCache.lastCheck = 0
}

// Get cache statistics for debugging
export const getCacheStats = () => {
  const now = Date.now()
  return {
    hasData: !!basicReportCache.data,
    age: now - basicReportCache.timestamp,
    timeSinceLastCheck: now - basicReportCache.lastCheck,
    isExpired: now - basicReportCache.timestamp >= basicReportCache.CACHE_DURATION,
    shouldCheck: now - basicReportCache.lastCheck >= basicReportCache.CHECK_INTERVAL,
    reportCount: basicReportCache.data?.reports?.length || 0,
  }
}
